import React, { useState, useCallback } from 'react';
import {
  X, Download, Save, FileText, Type, List, ListOrdered,
  Table2, Quote, Columns, AlertCircle, Minus, ChevronDown,
  ChevronRight, Palette, Loader2, Pencil, Eye
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

/* ─── Theme palette (matches backend THEMES keys) ─── */
const THEME_COLORS = {
  professional_blue: { primary: '#1E3A5F', accent: '#FF6B35', bg: '#EBF5FB', label: 'Professional Blue' },
  executive_dark:    { primary: '#1A1A2E', accent: '#E94560', bg: '#F8F9FA', label: 'Executive Dark' },
  modern_green:      { primary: '#1B4332', accent: '#52B788', bg: '#F0FAF3', label: 'Modern Green' },
  warm_terracotta:   { primary: '#8B3A2A', accent: '#D4825A', bg: '#FAF6F2', label: 'Warm Terracotta' },
  clean_minimal:     { primary: '#263238', accent: '#607D8B', bg: '#F5F5F5', label: 'Clean Minimal' },
  corporate_violet:  { primary: '#3B0764', accent: '#9333EA', bg: '#F8F3FF', label: 'Corporate Violet' },
  ocean_teal:        { primary: '#006D7A', accent: '#02B4A0', bg: '#EDFBFC', label: 'Ocean Teal' },
  bold_red:          { primary: '#8B0000', accent: '#E8304A', bg: '#FFF5F5', label: 'Bold Red' },
};

/* ─── Section type icons ─── */
const SECTION_ICONS = {
  title_page: FileText,
  heading: Type,
  paragraph: FileText,
  bullet_list: List,
  numbered_list: ListOrdered,
  table: Table2,
  quote: Quote,
  callout_box: AlertCircle,
  two_column: Columns,
  page_break: Minus,
};

/* ─── Section labels ─── */
const SECTION_LABELS = {
  title_page: 'Title Page',
  heading: 'Heading',
  paragraph: 'Paragraph',
  bullet_list: 'Bullet List',
  numbered_list: 'Numbered List',
  table: 'Table',
  quote: 'Quote',
  callout_box: 'Callout Box',
  two_column: 'Two Column',
  page_break: 'Page Break',
};


const DocViewerPanel = ({ docData, fileId, filename, onClose, onDownload, onSave, saving, onRenameDownload }) => {
  const [sections, setSections] = useState(docData?.sections || []);
  const [theme, setTheme] = useState(docData?.theme || 'professional_blue');
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const colors = THEME_COLORS[theme] || THEME_COLORS.professional_blue;

  const toggleCollapse = (index) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const updateSection = useCallback((index, updater) => {
    setSections(prev => {
      const next = [...prev];
      next[index] = typeof updater === 'function' ? updater(next[index]) : { ...next[index], ...updater };
      return next;
    });
    setHasChanges(true);
  }, []);

  const handleSave = () => {
    if (onSave) onSave({ theme, sections });
  };

  /* ─── View-mode renderers (clean, no inputs/labels) ─── */

  const viewTitlePage = (sec) => {
    if (!sec.title) return null;
    return (
      <div className="py-12 px-8 text-center" style={{ background: colors.bg }}>
        <h1 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>{sec.title}</h1>
        <div className="h-[2px] w-24 mx-auto mb-4" style={{ background: colors.accent }} />
        {sec.date && <p className="text-sm text-slate-500">{sec.date}</p>}
      </div>
    );
  };

  const viewHeading = (sec) => {
    const level = sec.level || 1;
    const Tag = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
    const sizes = { 1: 'text-2xl', 2: 'text-xl', 3: 'text-lg' };
    return (
      <div className="px-8 py-2">
        <Tag className={`${sizes[level]} font-bold`} style={{ color: colors.primary }}>{sec.text || ''}</Tag>
        {level === 1 && <div className="h-[2px] mt-1" style={{ background: colors.accent }} />}
      </div>
    );
  };

  const viewParagraph = (sec) => (
    <div className="px-8 py-1.5">
      <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">{sec.text || ''}</p>
    </div>
  );

  const viewBulletList = (sec) => (
    <div className="px-8 py-1.5">
      {sec.title && <p className="font-semibold text-sm mb-1" style={{ color: colors.primary }}>{sec.title}</p>}
      <ul className="list-disc pl-6 space-y-0.5">
        {(sec.items || []).map((item, i) => (
          <li key={i} className="text-sm text-slate-700">{typeof item === 'string' ? item : item.text || ''}</li>
        ))}
      </ul>
    </div>
  );

  const viewNumberedList = (sec) => (
    <div className="px-8 py-1.5">
      {sec.title && <p className="font-semibold text-sm mb-1" style={{ color: colors.primary }}>{sec.title}</p>}
      <ol className="list-decimal pl-6 space-y-0.5">
        {(sec.items || []).map((item, i) => (
          <li key={i} className="text-sm text-slate-700">{typeof item === 'string' ? item : item.text || ''}</li>
        ))}
      </ol>
    </div>
  );

  const viewTable = (sec) => {
    const headers = sec.headers || [];
    const rows = sec.rows || [];
    return (
      <div className="px-8 py-2">
        {sec.title && <p className="font-semibold text-sm mb-1" style={{ color: colors.primary }}>{sec.title}</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            {headers.length > 0 && (
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th key={hi} className="px-3 py-2 text-left text-white text-xs font-medium" style={{ background: colors.primary }}>{h}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  {(row || []).map((cell, ci) => (
                    <td key={ci} className="px-3 py-1.5 border-b border-slate-200 text-slate-700 text-xs">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const viewQuote = (sec) => (
    <div className="px-8 py-2">
      <blockquote className="border-l-4 pl-4 py-2 italic text-sm text-slate-600" style={{ borderColor: colors.accent }}>
        {sec.text || ''}
        {sec.attribution && <footer className="text-xs text-slate-400 mt-1 not-italic">{sec.attribution}</footer>}
      </blockquote>
    </div>
  );

  const viewCalloutBox = (sec) => (
    <div className="px-8 py-2">
      <div className="rounded border p-4" style={{ background: colors.bg, borderColor: colors.accent }}>
        <p className="font-semibold text-sm mb-1" style={{ color: colors.accent }}>{sec.title || 'Note'}</p>
        <p className="text-sm text-slate-700">{sec.text || ''}</p>
      </div>
    </div>
  );

  const viewTwoColumn = (sec) => (
    <div className="px-8 py-2">
      <div className="grid grid-cols-2 gap-4">
        {['left', 'right'].map(side => {
          const col = sec[side] || {};
          return (
            <div key={side}>
              {col.title && <p className="font-semibold text-sm mb-1" style={{ color: colors.primary }}>{col.title}</p>}
              <p className="text-sm text-slate-700">{col.content || ''}</p>
            </div>
          );
        })}
      </div>
    </div>
  );

  const viewPageBreak = () => (
    <div className="py-4">
      <div className="border-t border-slate-200" />
    </div>
  );

  const VIEW_RENDERERS = {
    title_page: viewTitlePage,
    heading: viewHeading,
    paragraph: viewParagraph,
    bullet_list: viewBulletList,
    numbered_list: viewNumberedList,
    table: viewTable,
    quote: viewQuote,
    callout_box: viewCalloutBox,
    two_column: viewTwoColumn,
    page_break: viewPageBreak,
  };

  /* ─── Per-section inline editors (edit mode) ─── */

  const renderTitlePage = (sec, idx) => (
    <div className="space-y-4 p-6 text-center" style={{ background: colors.bg }}>
      <input
        className="w-full text-center text-3xl font-bold bg-transparent border-b-2 border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none px-2 py-1 transition-colors"
        style={{ color: colors.primary }}
        value={sec.title || ''}
        onChange={e => updateSection(idx, { title: e.target.value })}
        placeholder="Document Title"
      />
      <input
        className="w-full text-center text-lg italic bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none px-2 py-1 text-slate-500 transition-colors"
        value={sec.subtitle || ''}
        onChange={e => updateSection(idx, { subtitle: e.target.value })}
        placeholder="Subtitle"
      />
      <div className="h-[2px] w-24 mx-auto" style={{ background: colors.accent }} />
      <div className="flex justify-center gap-3 text-sm text-slate-500">
        <input
          className="bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none px-1 text-center transition-colors"
          value={sec.author || ''}
          onChange={e => updateSection(idx, { author: e.target.value })}
          placeholder="Author"
        />
        <span>|</span>
        <input
          className="bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none px-1 text-center transition-colors"
          value={sec.company || ''}
          onChange={e => updateSection(idx, { company: e.target.value })}
          placeholder="Company"
        />
        <span>|</span>
        <input
          className="bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none px-1 text-center transition-colors"
          value={sec.date || ''}
          onChange={e => updateSection(idx, { date: e.target.value })}
          placeholder="Date"
        />
      </div>
    </div>
  );

  const renderHeading = (sec, idx) => {
    const level = sec.level || 1;
    const sizes = { 1: 'text-2xl', 2: 'text-xl', 3: 'text-lg' };
    return (
      <div className="px-6 py-2">
        <div className="flex items-center gap-2">
          <select
            value={level}
            onChange={e => updateSection(idx, { level: parseInt(e.target.value) })}
            className="text-xs bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-500"
          >
            <option value={1}>H1</option>
            <option value={2}>H2</option>
            <option value={3}>H3</option>
          </select>
          <input
            className={`flex-1 ${sizes[level]} font-bold bg-transparent border-b-2 border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none px-1 py-1 transition-colors`}
            style={{ color: colors.primary }}
            value={sec.text || ''}
            onChange={e => updateSection(idx, { text: e.target.value })}
            placeholder={`Heading ${level}`}
          />
        </div>
        {level === 1 && <div className="h-[2px] mt-1" style={{ background: colors.accent }} />}
      </div>
    );
  };

  const renderParagraph = (sec, idx) => (
    <div className="px-6 py-2">
      <textarea
        className="w-full bg-transparent border border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none rounded px-2 py-1.5 text-sm leading-relaxed text-slate-700 resize-y min-h-[60px] transition-colors"
        value={sec.text || ''}
        onChange={e => updateSection(idx, { text: e.target.value })}
        placeholder="Paragraph text..."
        rows={Math.max(3, Math.ceil((sec.text || '').length / 80))}
      />
    </div>
  );

  const renderBulletList = (sec, idx) => (
    <div className="px-6 py-2">
      {sec.title && (
        <input
          className="w-full font-semibold text-sm bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none px-1 py-0.5 mb-2 transition-colors"
          style={{ color: colors.primary }}
          value={sec.title}
          onChange={e => updateSection(idx, { title: e.target.value })}
          placeholder="List title"
        />
      )}
      <ul className="space-y-1">
        {(sec.items || []).map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-slate-400 mt-1.5 text-xs">•</span>
            <input
              className="flex-1 text-sm bg-transparent border-b border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none px-1 py-0.5 text-slate-700 transition-colors"
              value={typeof item === 'string' ? item : item.text || ''}
              onChange={e => {
                const newItems = [...(sec.items || [])];
                newItems[i] = e.target.value;
                updateSection(idx, { items: newItems });
              }}
            />
            <button
              className="text-slate-300 hover:text-red-400 mt-1"
              onClick={() => {
                const newItems = (sec.items || []).filter((_, j) => j !== i);
                updateSection(idx, { items: newItems });
              }}
            >
              <X className="w-3 h-3" />
            </button>
          </li>
        ))}
      </ul>
      <button
        className="text-xs text-blue-500 hover:text-blue-700 mt-2 flex items-center gap-1"
        onClick={() => updateSection(idx, { items: [...(sec.items || []), 'New item'] })}
      >
        + Add item
      </button>
    </div>
  );

  const renderNumberedList = (sec, idx) => (
    <div className="px-6 py-2">
      {sec.title && (
        <input
          className="w-full font-semibold text-sm bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none px-1 py-0.5 mb-2 transition-colors"
          style={{ color: colors.primary }}
          value={sec.title}
          onChange={e => updateSection(idx, { title: e.target.value })}
          placeholder="List title"
        />
      )}
      <ol className="space-y-1">
        {(sec.items || []).map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-slate-400 mt-1 text-xs font-medium w-4 text-right">{i + 1}.</span>
            <input
              className="flex-1 text-sm bg-transparent border-b border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none px-1 py-0.5 text-slate-700 transition-colors"
              value={typeof item === 'string' ? item : item.text || ''}
              onChange={e => {
                const newItems = [...(sec.items || [])];
                newItems[i] = e.target.value;
                updateSection(idx, { items: newItems });
              }}
            />
            <button
              className="text-slate-300 hover:text-red-400 mt-1"
              onClick={() => {
                const newItems = (sec.items || []).filter((_, j) => j !== i);
                updateSection(idx, { items: newItems });
              }}
            >
              <X className="w-3 h-3" />
            </button>
          </li>
        ))}
      </ol>
      <button
        className="text-xs text-blue-500 hover:text-blue-700 mt-2 flex items-center gap-1"
        onClick={() => updateSection(idx, { items: [...(sec.items || []), 'New item'] })}
      >
        + Add item
      </button>
    </div>
  );

  const renderTable = (sec, idx) => {
    const headers = sec.headers || [];
    const rows = sec.rows || [];
    return (
      <div className="px-6 py-2">
        {sec.title && (
          <input
            className="w-full font-semibold text-sm bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none px-1 py-0.5 mb-2 transition-colors"
            style={{ color: colors.primary }}
            value={sec.title}
            onChange={e => updateSection(idx, { title: e.target.value })}
            placeholder="Table title"
          />
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            {headers.length > 0 && (
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th
                      key={hi}
                      className="px-3 py-2 text-left text-white text-xs font-medium"
                      style={{ background: colors.primary }}
                    >
                      <input
                        className="w-full bg-transparent border-none focus:outline-none text-white placeholder-white/60"
                        value={h}
                        onChange={e => {
                          const newH = [...headers];
                          newH[hi] = e.target.value;
                          updateSection(idx, { headers: newH });
                        }}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  {(row || []).map((cell, ci) => (
                    <td key={ci} className="px-3 py-1.5 border-b border-slate-200">
                      <input
                        className="w-full bg-transparent border-none focus:outline-none text-slate-700 text-xs"
                        value={cell}
                        onChange={e => {
                          const newRows = rows.map((r, rri) =>
                            rri === ri ? r.map((c, cci) => cci === ci ? e.target.value : c) : r
                          );
                          updateSection(idx, { rows: newRows });
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderQuote = (sec, idx) => (
    <div className="px-6 py-2">
      <div className="border-l-4 pl-4 py-2" style={{ borderColor: colors.accent }}>
        <textarea
          className="w-full bg-transparent border-none focus:outline-none text-slate-600 italic text-sm resize-none min-h-[40px]"
          value={sec.text || ''}
          onChange={e => updateSection(idx, { text: e.target.value })}
          placeholder="Quote text..."
          rows={2}
        />
        <input
          className="w-full bg-transparent border-none focus:outline-none text-slate-400 text-xs mt-1"
          value={sec.attribution || ''}
          onChange={e => updateSection(idx, { attribution: e.target.value })}
          placeholder="— Attribution"
        />
      </div>
    </div>
  );

  const renderCalloutBox = (sec, idx) => (
    <div className="px-6 py-2">
      <div className="rounded border p-4" style={{ background: colors.bg, borderColor: colors.accent }}>
        <div className="flex items-center gap-2 mb-2">
          <input
            className="w-8 bg-transparent border-none focus:outline-none text-center"
            value={sec.icon || '💡'}
            onChange={e => updateSection(idx, { icon: e.target.value })}
          />
          <input
            className="flex-1 font-semibold text-sm bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none transition-colors"
            style={{ color: colors.accent }}
            value={sec.title || ''}
            onChange={e => updateSection(idx, { title: e.target.value })}
            placeholder="Callout title"
          />
        </div>
        <textarea
          className="w-full bg-transparent border-none focus:outline-none text-slate-700 text-sm resize-none min-h-[40px]"
          value={sec.text || ''}
          onChange={e => updateSection(idx, { text: e.target.value })}
          placeholder="Callout content..."
          rows={2}
        />
      </div>
    </div>
  );

  const renderTwoColumn = (sec, idx) => (
    <div className="px-6 py-2">
      <div className="grid grid-cols-2 gap-4">
        {['left', 'right'].map(side => {
          const col = sec[side] || {};
          return (
            <div key={side} className="border border-slate-200 rounded p-3">
              <input
                className="w-full font-semibold text-sm bg-transparent border-b border-transparent hover:border-slate-300 focus:border-blue-500 focus:outline-none mb-2 transition-colors"
                style={{ color: colors.primary }}
                value={col.title || ''}
                onChange={e => updateSection(idx, { [side]: { ...col, title: e.target.value } })}
                placeholder={`${side === 'left' ? 'Left' : 'Right'} column title`}
              />
              <textarea
                className="w-full bg-transparent border-none focus:outline-none text-slate-700 text-sm resize-none min-h-[60px]"
                value={col.content || ''}
                onChange={e => updateSection(idx, { [side]: { ...col, content: e.target.value } })}
                placeholder="Column content..."
                rows={3}
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderPageBreak = () => (
    <div className="px-6 py-4 flex items-center gap-3 text-slate-400 text-xs">
      <div className="flex-1 border-t border-dashed border-slate-300" />
      <span>Page Break</span>
      <div className="flex-1 border-t border-dashed border-slate-300" />
    </div>
  );

  const RENDERERS = {
    title_page: renderTitlePage,
    heading: renderHeading,
    paragraph: renderParagraph,
    bullet_list: renderBulletList,
    numbered_list: renderNumberedList,
    table: renderTable,
    quote: renderQuote,
    callout_box: renderCalloutBox,
    two_column: renderTwoColumn,
    page_break: renderPageBreak,
  };

  return (
    <div className="h-full flex flex-col bg-white border-l border-slate-200">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-[#F87217]" />
          <div>
            <h3 className="text-sm font-semibold text-slate-900 truncate max-w-[200px]">
              {filename || 'Document'}
            </h3>
            <p className="text-xs text-slate-500">
              {sections.length} sections • {isEditMode ? 'Edit Mode' : 'View Mode'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Edit/View toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (isEditMode) setShowThemePicker(false);
              setIsEditMode(!isEditMode);
            }}
            className="h-8 px-2 text-xs"
            title={isEditMode ? 'Switch to view mode' : 'Enable editing'}
          >
            {isEditMode ? <Eye className="w-4 h-4 mr-1" /> : <Pencil className="w-4 h-4 mr-1" />}
            {isEditMode ? 'View' : 'Edit'}
          </Button>
          {/* Theme picker toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowThemePicker(!showThemePicker)}
            disabled={!isEditMode}
            className="h-8 w-8 p-0 disabled:opacity-40"
            title="Change theme"
          >
            <Palette className="w-4 h-4 text-slate-500" />
          </Button>
          {/* Save */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSave}
            disabled={!isEditMode || !hasChanges || saving}
            className={`h-8 px-2 text-xs ${isEditMode && hasChanges ? 'text-blue-600 hover:text-blue-800' : 'text-slate-400'}`}
            title="Save changes"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          </Button>
          {/* Rename + Download */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRenameDownload?.(fileId, filename)}
            className="h-8 w-8 p-0 text-slate-500 hover:text-[#F87217]"
            title="Rename and download .docx"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          {/* Download */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDownload?.(fileId, filename)}
            className="h-8 w-8 p-0 text-slate-500 hover:text-[#F87217]"
            title="Download .docx"
          >
            <Download className="w-4 h-4" />
          </Button>
          {/* Close */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Theme picker dropdown */}
      {isEditMode && showThemePicker && (
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <p className="text-xs text-slate-500 mb-2 font-medium">Document Theme</p>
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(THEME_COLORS).map(([key, val]) => (
              <button
                key={key}
                onClick={() => { setTheme(key); setHasChanges(true); setShowThemePicker(false); }}
                className={`flex flex-col items-center gap-1 p-2 rounded border text-xs transition-all ${
                  theme === key ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex gap-0.5">
                  <div className="w-3 h-3 rounded-sm" style={{ background: val.primary }} />
                  <div className="w-3 h-3 rounded-sm" style={{ background: val.accent }} />
                </div>
                <span className="text-[10px] text-slate-600 truncate w-full text-center">{val.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Document body */}
      <ScrollArea className="flex-1">
        <div className="py-4 max-w-[700px] mx-auto">
          {/* Document paper effect */}
          <div className="bg-white shadow-sm border border-slate-200 mx-4 rounded-sm overflow-hidden">
            {sections.map((sec, idx) => {
              const SecIcon = SECTION_ICONS[sec.type] || FileText;
              const label = SECTION_LABELS[sec.type] || sec.type;
              const isCollapsed = collapsedSections.has(idx);
              const editRenderer = RENDERERS[sec.type];
              const viewRenderer = VIEW_RENDERERS[sec.type];

              return (
                <div
                  key={idx}
                  className={isEditMode ? 'border-b border-slate-100 last:border-b-0 group' : ''}
                >
                  {/* Section header bar — only in edit mode */}
                  {isEditMode && (
                    <div className="flex items-center gap-2 px-4 py-1 bg-slate-50/0 group-hover:bg-slate-50 transition-colors">
                      <button
                        onClick={() => toggleCollapse(idx)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        {isCollapsed
                          ? <ChevronRight className="w-3.5 h-3.5" />
                          : <ChevronDown className="w-3.5 h-3.5" />
                        }
                      </button>
                      <SecIcon className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">{label}</span>
                    </div>
                  )}

                  {/* Section content */}
                  {isEditMode ? (
                    !isCollapsed && editRenderer && (
                      <div className="pb-2">
                        {editRenderer(sec, idx)}
                      </div>
                    )
                  ) : (
                    viewRenderer && viewRenderer(sec, idx)
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </ScrollArea>

      {/* Unsaved changes bar */}
      {isEditMode && hasChanges && (
        <div className="px-4 py-2.5 border-t border-amber-200 bg-amber-50 flex items-center justify-between">
          <span className="text-xs text-amber-700">Unsaved changes</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => {
                setSections(docData?.sections || []);
                setTheme(docData?.theme || 'professional_blue');
                setHasChanges(false);
              }}
            >
              Revert
            </Button>
            <Button
              size="sm"
              className="h-7 text-xs bg-[#F87217] hover:bg-[#C65B12] text-white"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              Save & Rebuild
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocViewerPanel;
